<script lang="ts">
import type {
	SelectEmits,
	SelectItem,
	SelectSlots,
	SelectProps as USelectProps,
} from '#ui/types';
import type { ArrayOrNested, GetItemKeys } from '#ui/types/utils';

export interface SelectProps<T extends ArrayOrNested<SelectItem> = ArrayOrNested<SelectItem>, VK extends GetItemKeys<T> = 'value', M extends boolean = false> extends USelectProps<T, VK, M> {
	readonly?: boolean;
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SelectItem>, VK extends GetItemKeys<T> = 'value', M extends boolean = false">
import { reactiveOmit } from '@vueuse/core';
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useReadonly } from '#layers/ui/composables/use-readonly';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import Select from '#ui/components/Select.vue';

const props = defineProps<SelectProps<T, VK, M>>();
const emit = defineEmits<SelectEmits<T, VK, M>>();
const slots = defineSlots<SelectSlots>();
const { forwardRef } = useForwardExpose();
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);

const forwarded = useForwardPropsEmits(reactiveOmit(props, 'readonly'), emit);
const trailingIcon = computed(() => !props.readonly ? props.trailingIcon : '');
</script>

<template>
	<Select
		:ref="forwardRef"
		v-bind="forwarded"
		:trailing-icon
		:ui="mergeUi(props.ui, {
			base: props.readonly ? 'cursor-default' : '',
		})"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="preventKeyboardNavigation"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
	</Select>
</template>
